import { assignDisplayTestIds, isExactTemplateMatch } from '@suvoda-uat/domain';
import {
  type CompletionRollup,
  type IncludeState,
  type StructureNote,
  type TestCaseRow,
  summariseOrigins,
  templateMatchLabel,
  templateMatchPct,
  toRecommendation,
} from '@suvoda-uat/ui-suvoda';
import type { Section, StructureState, Subsection, TestCase, TestScript } from './api-client.js';

export type { CompletionRollup } from '@suvoda-uat/ui-suvoda';

/**
 * Pure mapping layer that feeds the live ListTestScript (US1) into the
 * `ui-suvoda` Assembly components. No React here — the engine→presentation
 * translation (origin counts→Template Match %, decision counts, completion
 * progress) is unit-tested directly, and the component stays thin wiring over
 * the mutation hooks.
 */

/** Every Test Case in the script, flattened across Sections → Subsections. */
const allTestCases = (script: TestScript): readonly TestCase[] =>
  script.sections.flatMap((section) => section.subsections.flatMap((sub) => sub.testCases));

/**
 * Every Test Case keyed by its Subsection id, built from the UNFILTERED script
 * tree. Subsection-level actions (reverse / include / exclude — feature 026)
 * look up their cases here so they cascade over the CANONICAL subsection
 * regardless of any active view lens (FR-013 / SC-007) — never the lens-filtered
 * `deriveAssemblyView` rows. An instructional / empty subsection maps to `[]`.
 */
export const casesBySubsectionId = (
  script: TestScript,
): ReadonlyMap<string, readonly TestCase[]> => {
  const map = new Map<string, readonly TestCase[]>();
  for (const section of script.sections) {
    for (const sub of section.subsections) {
      map.set(sub.id, sub.testCases);
    }
  }
  return map;
};

/**
 * F-146 (FR-002/003) + #172: the actionable (qualifying) set for the subsection
 * bulk-approve — the cases the "Approve matches" control includes in one action.
 * A case qualifies iff it is ALL of: currently **undecided**, **AI-recommended for
 * inclusion** (#172 — `toRecommendation === 'include'`, i.e. Selection Decision
 * 'selected'; an AI-recommend-**exclude** or **ambiguous** case is left undecided
 * for per-case triage), AND a **byte-for-byte template match** (every Content Item
 * origin=template, ≥1 item). The byte-for-byte threshold is the pure
 * `packages/domain` predicate (`isExactTemplateMatch`, mutation-tested there) over
 * the existing `summariseOrigins` count; the recommendation check reuses the
 * existing `toRecommendation` SSOT. This composition adds only the `undecided` and
 * recommendation filters, so an already-included case is a no-op, an
 * already-excluded case is never overridden (SC-002), and no near-match is swept in
 * (FR-004/SC-003). #172 reverses F-146's "recommendation independence": the action
 * can no longer auto-include a case the engine flagged Exclude or left ambiguous.
 * Input order is preserved (`Array.prototype.filter`). The caller fans the existing
 * per-case Include over this set; an optimistic flip to `included` empties it, so
 * the control vanishes (clarify 2026-06-28). The SAME filter drives the control's
 * count + visibility, so both narrow with it automatically (FR-006).
 */
export const qualifyingExactMatchCases = (cases: readonly TestCase[]): readonly TestCase[] =>
  cases.filter(
    (testCase) =>
      testCase.inclusionDecision.state === 'undecided' &&
      toRecommendation(testCase) === 'include' &&
      isExactTemplateMatch(summariseOrigins(testCase.content.rows)),
  );

/** A designer Inclusion Decision state → the table's tri-state include flag. */
export const toIncludeState = (state: TestCase['inclusionDecision']['state']): IncludeState =>
  state === 'undecided' ? 'none' : state;

/**
 * F-017 (US2): a node's honest Structure State → its presentational reason
 * chip, or `null` for the silent template-home happy path (chipping every
 * placed node would bury the real signals). The machine-generated `reason`
 * becomes the chip's accessible tooltip; the short label is the at-a-glance
 * signal. Exhaustive over the reason-code union — a new code is a type error
 * here until it is given a surface. The argument is REQUIRED: every served node
 * carries a Structure State (the contract guarantees it; the API projects it on
 * every read), so a missing state is a bug to surface, never to swallow.
 */
export const structureChip = (state: StructureState): StructureNote | null => {
  const detail = state.reason;
  switch (state.reasonCode) {
    case 'template_home':
      return null;
    case 'variant_excluded_by_rule':
      return { label: 'Excluded by rule', tone: 'excluded', detail };
    case 'excluded_by_reviewer':
      return { label: 'Excluded', tone: 'excluded', detail };
    case 'no_cases_selected':
      return { label: 'No cases selected', tone: 'empty', detail };
    case 'all_cases_excluded_by_reviewer':
      return { label: 'All cases excluded', tone: 'empty', detail };
    case 'study_specific_pending_authoring':
      return { label: 'Needs authoring', tone: 'needs-authoring', detail };
    case 'variant_included_low_confidence':
      return { label: 'Confirm variant', tone: 'review', detail };
    case 'reviewer_override':
      return { label: 'Overridden', tone: 'override', detail };
    // F-151 (US2): authoritative scope exclusions reuse the existing "excluded"
    // tone; the label names the source (TRS verdict vs learned prediction) and
    // the machine reason carries the verdict value (FR-007). Non-colour-only.
    case 'excluded_by_trs_scope':
      return { label: 'Excluded — TRS', tone: 'excluded', detail };
    case 'excluded_by_learned_scope':
      return { label: 'Excluded — learned', tone: 'excluded', detail };
    default: {
      const exhaustive: never = state.reasonCode;
      return exhaustive;
    }
  }
};

/**
 * F-151 (US2): the quiet "advisory" cue for a below-gate / unmeasured
 * out-of-scope TRS verdict (the node is NOT excluded — it keeps its normal
 * state). Distinct from an exclusion: tone `review` (a warning Pill), so it
 * reads as "the TRS flagged this, take a look" rather than "removed". Returns
 * null when no advisory applies. Non-colour-only — the label names the source.
 */
export const scopeAdvisoryChip = (advisory: Subsection['scopeAdvisory']): StructureNote | null => {
  if (!advisory) return null;
  const source = advisory.source === 'learned' ? 'learned' : 'TRS';
  return { label: `Advisory — ${source}`, tone: 'review', detail: advisory.reason };
};

/**
 * The canonical display name for a Test Case (F-027 / UAT-12): the descriptive
 * name (glossary "Title (Test Case)") when present, else the Function, else the
 * Test ID. Always non-empty — `testId` is always present (FR-003 / SC-004). The
 * single source of the name shown in the subsection list's Test Case column, in
 * the search needle (FR-008), and — via the detail/edit views that import it —
 * everywhere a case is named, so all surfaces stay consistent (FR-002 / SC-003).
 */
export const testCaseDisplayName = (testCase: TestCase): string =>
  testCase.title ?? testCase.function ?? testCase.testId;

/**
 * Map one live contract Test Case onto the presentational TestCaseTable row.
 * When the case carries a Displayed Test ID (assigned by `deriveAssemblyView`
 * over its subsection's curated order — F-023), it rides onto the row as the
 * positional primary; the original `testId` stays as the secondary handle and
 * the key every aria/`data-*` hook + routing lookup uses (R4/FR-004).
 */
export const toTestCaseRow = (
  testCase: TestCase & { readonly displayTestId?: string },
): TestCaseRow => {
  // Template Match is derived from the served Content Items' Origins — NOT
  // from selectionDecision.score, which is the engine's requirements-match
  // strength and is uncorrelated with how much content came from the template
  // (ubiquitous boilerplate cases legitimately score lowest).
  const origins = summariseOrigins(testCase.content.rows);
  // F-017 (US2): surface a non-default structure state on the row (excluded /
  // variant-excluded) — never hide the row.
  const note = structureChip(testCase.structureState);
  return {
    id: testCase.id,
    testId: testCase.testId,
    ...(testCase.displayTestId !== undefined ? { displayTestId: testCase.displayTestId } : {}),
    function: testCase.function ?? 'Unassigned',
    roles: [...testCase.roles],
    // F-027 (FR-001) + F-017 (US1): the Test Case column shows the OCCURRENCE's
    // own descriptive name (canonical name → Function → Test ID fallback), NOT
    // the Function — the Function rides its own column above (FR-004); collider
    // siblings thus display their distinct titles.
    testCase: testCaseDisplayName(testCase),
    templateMatch: templateMatchPct(origins),
    templateMatchLabel: templateMatchLabel(origins),
    novel: testCase.classification === 'novel',
    // The AI recommendation drives the toggle OUTLINE; the designer's decision the FILL.
    recommendation: toRecommendation(testCase),
    includeState: toIncludeState(testCase.inclusionDecision.state),
    completed: testCase.completionState.state === 'complete',
    ...(note ? { structureNote: note } : {}),
    // Content Items (steps) for the inline dropdown preview (Figma 1518-80731).
    contentItems: testCase.content.rows.map((item) => ({
      id: item.id,
      body: item.body,
      kind: item.kind,
      origin: item.origin,
    })),
  };
};

/**
 * The Displayed Test ID (F-023 positional id) for ONE Test Case — used by the
 * detail/edit headers, which locate a case by route token (original handle or
 * internal id, R4) and need its number without going through the assembly view.
 * Numbers the case's home subsection over its curated order (the same shared
 * `packages/domain` rule the list + render payload use, so all surfaces agree)
 * and returns that case's positional id; falls back to the original handle if
 * the case isn't found (defensive — the caller already located it). The lookup
 * keys on `id`, never the positional number (FR-004/I-TIN-06).
 */
export const displayTestIdFor = (script: TestScript, testCase: TestCase): string => {
  for (const section of script.sections) {
    for (const sub of section.subsections) {
      if (sub.testCases.some((candidate) => candidate.id === testCase.id)) {
        const numbered = assignDisplayTestIds(sub.testCases);
        return (
          numbered.find((candidate) => candidate.id === testCase.id)?.displayTestId ??
          testCase.testId
        );
      }
    }
  }
  return testCase.testId;
};

/** A count of the engine's selection decisions across the whole Test Script. */
export type SelectionSummary = {
  readonly total: number;
  readonly selected: number;
  readonly ambiguous: number;
  readonly excluded: number;
};

export const summariseSelection = (script: TestScript): SelectionSummary => {
  const cases = allTestCases(script);
  const count = (decision: TestCase['selectionDecision']['decision']): number =>
    cases.filter((testCase) => testCase.selectionDecision.decision === decision).length;
  return {
    total: cases.length,
    selected: count('selected'),
    ambiguous: count('ambiguous'),
    excluded: count('excluded'),
  };
};

/** The header line summarising the engine's recommendations for the Test Script. */
export const statsLine = (summary: SelectionSummary): string =>
  `${summary.total} test cases identified • ${summary.ambiguous} require review • ${summary.selected} recommended • ${summary.excluded} not recommended`;

// ── Feature 025: Completion Rollup derivation ───────────────────────────────
//
// A derived, display-only "Marked as Complete X/Y" summary for a Subsection or
// Section (and the whole-script header). One uniform rule at every level
// (I-SCR-09): `available` (the denominator Y) counts the Test Cases that are
// non-excluded AND content-bearing (Option B, FR-003); `complete` (the
// numerator X) counts the AVAILABLE cases that are also marked complete
// (FR-004). `isComplete` is defined THROUGH `isAvailable`, so X ≤ Y by
// construction — a completed-then-emptied case (completion is sticky across a
// content-clearing edit) and an excluded-but-complete case each count toward
// NEITHER side (I-SCR-03). NB: distinct from the lens predicate `isCompleted`
// (below), which is ungated and drives hide-completed, not the rollup.

const isExcluded = (testCase: TestCase): boolean => testCase.inclusionDecision.state === 'excluded';
const hasContent = (testCase: TestCase): boolean => testCase.content.rows.length > 0;
/** "Available to review" — the denominator predicate (non-excluded ∧ content-bearing). */
const isAvailable = (testCase: TestCase): boolean => !isExcluded(testCase) && hasContent(testCase);
/** The numerator predicate — defined through `isAvailable`, so complete ⊆ available. */
const isComplete = (testCase: TestCase): boolean =>
  isAvailable(testCase) && testCase.completionState.state === 'complete';

/** Roll a flat list of Test Cases up into a Completion Rollup `{ complete, available }`. */
export const completionRollupOf = (cases: readonly TestCase[]): CompletionRollup => ({
  complete: cases.filter(isComplete).length,
  available: cases.filter(isAvailable).length,
});

/**
 * F-149 (FR-011): how many cases are eligible for an interim (draft) export —
 * Included AND Complete (mirrors the API's `isCompletedAndIncluded`). The
 * "Download current progress" affordance is unavailable until this is ≥ 1, so no
 * empty document is ever produced (SC-006). Note this is the INTERIM filter, NOT
 * the completion rollup's `complete` (which counts non-excluded, including
 * undecided, complete cases).
 */
export const interimReadyCount = (script: TestScript): number =>
  allTestCases(script).filter(
    (testCase) =>
      testCase.inclusionDecision.state === 'included' &&
      testCase.completionState.state === 'complete',
  ).length;

/** The Completion Rollup for one Subsection (over its own Test Cases). */
export const subsectionCompletion = (subsection: Subsection): CompletionRollup =>
  completionRollupOf(subsection.testCases);

/** The Completion Rollup for one Section (flattened across all its Subsections). */
export const sectionCompletion = (section: Section): CompletionRollup =>
  completionRollupOf(section.subsections.flatMap((sub) => sub.testCases));

/**
 * Marked-as-Complete progress for the whole-script header. FR-006: the header
 * adopts the SAME non-excluded + content-bearing rule as the section/subsection
 * rollups (a deliberate change from the previous "count all cases"), so
 * excluding a case decrements the header total consistently with the indicators
 * (I-SCR-09). An all-excluded/empty script yields `total: 0`; the header's
 * progress bar already guards `total === 0` (UATPage).
 */
export const markedProgress = (
  script: TestScript,
): { readonly current: number; readonly total: number } => {
  const { complete, available } = completionRollupOf(allTestCases(script));
  return { current: complete, total: available };
};

// ── Feature 022: Assembly View Lens derivation ──────────────────────────────
//
// The reviewer's per-visit, view-only lens over the assembly tree: the search
// Test-ID filter, the hide-completed filter, and the Test-ID sort order. A pure
// derivation (no React) so every composition rule is unit-tested directly; the
// component stays thin wiring over it. Hide (US1) and Sort (US2) extend the
// pipeline below RED-first — this seam lands with search semantics preserved.

/** The Sort control's three-state cycle (`manual` = the script's own order). */
export type SortOrder = 'manual' | 'testIdAsc' | 'testIdDesc';

/** The ephemeral, never-persisted view lens (data-model.md §1.1). */
export type AssemblyViewLens = {
  /** Search needle — already lower-cased + trimmed by the caller. */
  readonly needle: string;
  /** Hide completed Test Cases (the Hide control). */
  readonly hideCompleted: boolean;
  /** Order rows by Test ID within each subsection (the Sort control). */
  readonly sortOrder: SortOrder;
};

/**
 * Why the rendered tree is empty: nothing (`none`), the search needle matched
 * no row (`search` — keeps today's message), or rows exist but Hide removed the
 * last visible one (`hidden` — the all-complete message). Drives R8 precedence.
 */
export type AssemblyEmptyReason = 'none' | 'search' | 'hidden';

/**
 * A Test Case annotated with its Displayed Test ID (F-023) — the case's own
 * handle prefix + its 1-based position in its subsection's curated order. A pure
 * presentation projection: intentionally NON-unique across subsections (every
 * subsection has a `…-01`) and never an identity — routing/lookup/aria key on
 * `id`/`testId`, never `displayTestId` (R4/I-TIN-06).
 */
export type DisplayTestCase = TestCase & { readonly displayTestId: string };
type DisplaySubsection = Omit<Subsection, 'testCases'> & {
  readonly testCases: readonly DisplayTestCase[];
};
type DisplaySection = Omit<Section, 'subsections'> & {
  readonly subsections: readonly DisplaySubsection[];
};

export type AssemblyView = {
  readonly visibleSections: readonly DisplaySection[];
  readonly emptyReason: AssemblyEmptyReason;
};

/** "Completed" predicate — the same one `toTestCaseRow`/`markedProgress` use (R4). */
const isCompleted = (testCase: TestCase): boolean => testCase.completionState.state === 'complete';

/** True when any subsection anywhere still carries at least one Test Case row. */
const hasAnyRow = (sections: readonly DisplaySection[]): boolean =>
  sections.some((section) => section.subsections.some((sub) => sub.testCases.length > 0));

/**
 * Assign the Displayed Test ID to every Test Case, per subsection, over its
 * CURATED order (sorted by the gap-tolerant `order` field) — F-023's first
 * derivation step, before any view lens, so the number is the case's curated
 * home position and a transient hide/sort/search never recomputes it
 * (R2/I-TIN-04). Uses the single shared `packages/domain` numbering rule — the
 * same one the finalise render payload uses, so app and document agree (SC-004).
 */
const assignDisplayTestIdsToTree = (sections: readonly Section[]): readonly DisplaySection[] =>
  sections.map((section) => ({
    ...section,
    subsections: section.subsections.map((sub) => ({
      ...sub,
      testCases: assignDisplayTestIds(sub.testCases),
    })),
  }));

/**
 * Keep only rows satisfying `keep`, then drop the groupings that empty out:
 * subsections with no surviving rows, then sections with no surviving
 * subsections. The shared shape behind both the search and hide filters, so a
 * purely instructional (row-less) grouping drops identically under either.
 */
const filterRows = (
  sections: readonly DisplaySection[],
  keep: (testCase: TestCase) => boolean,
): readonly DisplaySection[] =>
  sections
    .map((section) => ({
      ...section,
      subsections: section.subsections
        .map((sub) => ({ ...sub, testCases: sub.testCases.filter(keep) }))
        .filter((sub) => sub.testCases.length > 0),
    }))
    .filter((section) => section.subsections.length > 0);

/**
 * Natural Test-ID order (`2 < 10`); a row with an empty/whitespace `testId`
 * always sorts AFTER every non-empty row regardless of direction, and two empty
 * rows keep their relative order (R5/FR-006). Descending negates only the
 * non-empty comparison.
 */
const compareTestId = (a: TestCase, b: TestCase, descending: boolean): number => {
  const aEmpty = a.testId.trim() === '';
  const bEmpty = b.testId.trim() === '';
  if (aEmpty || bEmpty) return aEmpty === bEmpty ? 0 : aEmpty ? 1 : -1;
  const cmp = a.testId.localeCompare(b.testId, undefined, { numeric: true, sensitivity: 'base' });
  return descending ? -cmp : cmp;
};

/**
 * Stable-sort each subsection's rows by Test ID. Section and subsection order is
 * never touched — only the row arrays within a subsection re-order (I-AHS-04);
 * `Array.prototype.sort` is stable (ES2019+), which FR-006 leans on. Operates on
 * a copy, never the input.
 */
const sortRows = (
  sections: readonly DisplaySection[],
  order: 'testIdAsc' | 'testIdDesc',
): readonly DisplaySection[] => {
  const descending = order === 'testIdDesc';
  return sections.map((section) => ({
    ...section,
    subsections: section.subsections.map((sub) => ({
      ...sub,
      testCases: [...sub.testCases].sort((a, b) => compareTestId(a, b, descending)),
    })),
  }));
};

/**
 * Apply the view lens to the section tree: assign Displayed Test IDs (F-023) →
 * search filter → hide filter → drop emptied groupings → sort rows, returning
 * the survivors plus the reason the view is empty (if it is).
 *
 * The Displayed Test ID assignment is the FIRST step and runs on EVERY call
 * (even the default lens), so each row carries its curated-order number through
 * any subsequent lens and a transient hide/sort/search never recomputes it
 * (R2/I-TIN-04). The default lens therefore returns the freshly-numbered tree
 * rather than the input reference; `useMemo` (keyed on `sections`+lens) still
 * guards recomputation, and the numbering is an O(n) pass — negligible.
 *
 * `emptyReason` precedence (R8): when no row is visible, `'hidden'` wins when
 * removing Hide would reveal rows (search-survivors that Hide stripped),
 * otherwise `'search'` when a needle alone emptied the view, otherwise `'none'`
 * (a genuinely row-less script — no misleading all-hidden message). Sort never
 * changes which rows are visible, so it does not enter the precedence.
 */
export const deriveAssemblyView = (
  sections: readonly Section[],
  lens: AssemblyViewLens,
): AssemblyView => {
  // F-023: number each subsection over its curated order BEFORE the lens.
  const numbered = assignDisplayTestIdsToTree(sections);

  const filtering = lens.needle !== '';
  if (!filtering && !lens.hideCompleted && lens.sortOrder === 'manual') {
    return { visibleSections: numbered, emptyReason: 'none' };
  }

  const searched = filtering
    ? // F-027 (FR-008): match the displayed descriptive name as well as the Test
      // ID — "search matches what you see". The needle is already lower-cased +
      // trimmed by the caller. Sort order is untouched (still keyed on testId).
      filterRows(
        numbered,
        (testCase) =>
          testCase.testId.toLowerCase().includes(lens.needle) ||
          testCaseDisplayName(testCase).toLowerCase().includes(lens.needle),
      )
    : numbered;

  const filtered = lens.hideCompleted
    ? filterRows(searched, (testCase) => !isCompleted(testCase))
    : searched;

  let emptyReason: AssemblyEmptyReason = 'none';
  if (!hasAnyRow(filtered)) {
    if (hasAnyRow(searched)) emptyReason = 'hidden';
    else if (filtering) emptyReason = 'search';
  }

  const visibleSections =
    lens.sortOrder === 'manual' ? filtered : sortRows(filtered, lens.sortOrder);

  return { visibleSections, emptyReason };
};

// ── Feature 015: optimistic move application ────────────────────────────────

export type LocalMove =
  | { readonly kind: 'section'; readonly sectionId: string; readonly afterSectionId: string | null }
  | {
      readonly kind: 'subsection';
      readonly subsectionId: string;
      readonly afterSubsectionId: string | null;
    }
  | {
      readonly kind: 'testCase';
      readonly testCaseId: string;
      readonly toSubsectionId: string;
      readonly afterTestCaseId: string | null;
    };

const renumber = <T extends { readonly order: number }>(nodes: readonly T[]): T[] =>
  nodes.map((node, index) => ({ ...node, order: index }));

/** Remove `id`, splice it back after `afterId` (null ⇒ top); null ⇒ invalid. */
const movedAfter = <T extends { readonly id: string }>(
  list: readonly T[],
  node: T,
  afterId: string | null,
): T[] | null => {
  const remaining = list.filter((candidate) => candidate.id !== node.id);
  const at =
    afterId === null ? 0 : remaining.findIndex((candidate) => candidate.id === afterId) + 1;
  if (afterId !== null && at === 0) return null; // afterId not found
  const next = [...remaining];
  next.splice(at, 0, node);
  return next;
};

/** True when both lists carry the same ids in the same sequence. */
const sameSequence = (
  before: ReadonlyArray<{ readonly id: string }>,
  after: ReadonlyArray<{ readonly id: string }>,
): boolean => before.length === after.length && before.every((node, i) => node.id === after[i]?.id);

/**
 * Apply a move to the cached contract tree EXACTLY as the server will: the
 * destination sibling list renumbers to contiguous 0..n; a cross-subsection
 * source keeps its remaining orders (gap-tolerant — orders are sort keys, not
 * indexes); a NO-OP move (identical resulting sequence) returns the script
 * untouched, mirroring the server's changed:false short-circuit — it never
 * renumbers, so gapped orders stay exactly as stored. Invalid moves return
 * the script unchanged; the settled refetch reconciles regardless.
 */
export const applyMoveLocally = (script: TestScript, move: LocalMove): TestScript => {
  if (move.kind === 'section') {
    const moved = script.sections.find((section) => section.id === move.sectionId);
    if (moved === undefined) return script;
    const next = movedAfter(script.sections, moved, move.afterSectionId);
    if (next === null || sameSequence(script.sections, next)) return script;
    return { ...script, sections: renumber(next) };
  }

  if (move.kind === 'subsection') {
    const parent = script.sections.find((section) =>
      section.subsections.some((subsection) => subsection.id === move.subsectionId),
    );
    const moved = parent?.subsections.find((subsection) => subsection.id === move.subsectionId);
    if (parent === undefined || moved === undefined) return script;
    const next = movedAfter(parent.subsections, moved, move.afterSubsectionId);
    if (next === null || sameSequence(parent.subsections, next)) return script;
    const renumbered = renumber(next);
    return {
      ...script,
      sections: script.sections.map((section) =>
        section.id === parent.id ? { ...section, subsections: renumbered } : section,
      ),
    };
  }

  const allSubsections = script.sections.flatMap((section) => section.subsections);
  const source = allSubsections.find((subsection) =>
    subsection.testCases.some((testCase) => testCase.id === move.testCaseId),
  );
  const moved = source?.testCases.find((testCase) => testCase.id === move.testCaseId);
  const destination = allSubsections.find((subsection) => subsection.id === move.toSubsectionId);
  if (source === undefined || moved === undefined || destination === undefined) return script;

  const sameList = source.id === destination.id;
  const next = movedAfter(
    (sameList ? source : destination).testCases.filter(
      (testCase) => testCase.id !== move.testCaseId,
    ),
    { ...moved, subsectionId: destination.id },
    move.afterTestCaseId,
  );
  if (next === null) return script;
  if (sameList && sameSequence(source.testCases, next)) return script;
  const renumbered = renumber(next);
  return {
    ...script,
    sections: script.sections.map((section) => ({
      ...section,
      subsections: section.subsections.map((subsection) => {
        if (subsection.id === destination.id) return { ...subsection, testCases: renumbered };
        if (!sameList && subsection.id === source.id) {
          // Gap-tolerant source: remaining cases keep their orders.
          return {
            ...subsection,
            testCases: subsection.testCases.filter((testCase) => testCase.id !== move.testCaseId),
          };
        }
        return subsection;
      }),
    })),
  };
};

// ── Feature 029: optimistic per-case action reconciliation ──────────────────
//
// A per-case review action already RETURNS the updated Test Case (byte-identical
// to its slot inside ListTestScript — `toTestCaseResponse`), so the client
// reconciles its cache from the response instead of refetching the whole
// 354-case script. The six instant actions also paint an optimistic prediction
// before the round-trip; edit-content reconciles on the success splice only
// (its submitted rows lack the server-derived `origin`/`order` — R3). These pure
// helpers do the cache surgery; the hooks (`queries.ts`) wire them into the
// TanStack mutation lifecycle.

/**
 * FE mirror of the server's Inclusion Rollup rule
 * (`apps/api/src/domain/test-script-content/content.ts:255 computeInclusionRollup`):
 * empty → 'undecided'; any undecided → 'undecided'; all included → 'all_included';
 * all excluded → 'all_excluded'; otherwise (a no-undecided mix) → 'mixed'. Pinned
 * byte-for-byte by a parity test so a single-case splice recomputes the same
 * value the server would (FR-004/SC-006); the server stays authoritative via the
 * success splice. If this ever drifts, promote it to a shared `packages/domain`
 * rule (research R2).
 */
export const computeInclusionRollup = (
  cases: readonly TestCase[],
): Subsection['inclusionRollup'] => {
  if (cases.length === 0) return 'undecided';
  const states = cases.map((testCase) => testCase.inclusionDecision.state);
  if (states.some((state) => state === 'undecided')) return 'undecided';
  if (states.every((state) => state === 'included')) return 'all_included';
  if (states.every((state) => state === 'excluded')) return 'all_excluded';
  return 'mixed';
};

// ── Feature 145: coherent subsection include/exclude ─────────────────────────
//
// The subsection bulk control is driven by a BINARY derived presentation, not
// the tri-state rollup: a subsection reads "Excluded" iff every case is excluded
// and "In the script" otherwise. There is no third "partially excluded" control
// state — a mix is ordinary per-case curation. These pure helpers do the
// derivation + the guard's approved-case lookup; the cascade hook (`queries.ts`)
// and the view wire them in. Zero contract change: all reuse the existing
// per-case Include/Exclude/ClearInclusion ops and the server-computed rollup.

/** The subsection bulk control's two derived presentations (FR-001). */
export type SubsectionPresentation = 'in-script' | 'excluded';

/**
 * F-145 (FR-001/002, I-SI-02): derive the subsection bulk control's presentation
 * from the server-computed (and optimistically-recomputed) `inclusionRollup`.
 * `'excluded'` IFF every case is excluded (`all_excluded` — the whole subsection
 * is removed); otherwise `'in-script'` (the default/contributing presentation,
 * covering `all_included`, `mixed`, and `undecided`). The control reads ONLY this
 * value, so excluding individual cases (rollup `mixed`) never becomes a separate
 * "partially excluded" state — it stays "In the script" (the tri-state
 * group-header precedent: the container reflects its items, never contradicts
 * them). Because `computeInclusionRollup` is the SSOT and recomputes on every
 * per-case splice, this derivation stays correct under optimism for free.
 */
export const subsectionPresentation = (
  rollup: Subsection['inclusionRollup'],
): SubsectionPresentation => (rollup === 'all_excluded' ? 'excluded' : 'in-script');

/**
 * F-145 (FR-005, I-SI-05): the subsection's APPROVED cases — those the designer
 * has explicitly set to `included` (NOT the engine's AI recommendation, which
 * lives on `selectionDecision` and never counts as approval). The presence of ≥ 1
 * is the trigger for the warn-and-confirm guard before excluding a subsection;
 * the returned cases (count + identities) are named in the confirmation. Operates
 * over the CANONICAL case list it is handed (`casesBySubsectionId`, the unfiltered
 * tree — never the lens-filtered rows), preserving order.
 */
export const approvedCasesIn = (cases: readonly TestCase[]): readonly TestCase[] =>
  cases.filter((testCase) => testCase.inclusionDecision.state === 'included');

/**
 * Immutably replace one Test Case (by id) across the Section → Subsection tree,
 * recomputing ONLY the owning subsection's `inclusionRollup` (the FE twin of the
 * fake store's `replaceTestCase`, `fakes/test-script-store.ts:61`). The
 * subsection control renders from the server-computed `sub.inclusionRollup`
 * (`AssemblyReviewView.tsx`), so a single-case splice MUST recompute it or the
 * indicator goes stale; every other derived indicator (completion rollups,
 * `casesBySubsectionId`, `markedProgress`) reads the raw cases and updates for
 * free. A subsection that does not own the case is returned untouched (same
 * reference). `update` receives the current case and returns its replacement —
 * the per-action optimistic patch on `onMutate`, or `() => serverCase` on the
 * success/rollback splice. Returns the SAME script reference when the id is
 * absent (a true no-op — no spurious cache-change notification).
 */
export const replaceTestCaseInScript = (
  script: TestScript,
  testCaseId: string,
  update: (testCase: TestCase) => TestCase,
): TestScript => {
  const owns = script.sections.some((section) =>
    section.subsections.some((sub) => sub.testCases.some((testCase) => testCase.id === testCaseId)),
  );
  if (!owns) return script;
  return {
    ...script,
    sections: script.sections.map((section) => ({
      ...section,
      subsections: section.subsections.map((sub) => {
        if (!sub.testCases.some((testCase) => testCase.id === testCaseId)) return sub;
        const testCases = sub.testCases.map((testCase) =>
          testCase.id === testCaseId ? update(testCase) : testCase,
        );
        return { ...sub, testCases, inclusionRollup: computeInclusionRollup(testCases) };
      }),
    })),
  };
};

/**
 * The six instant actions' optimistic updaters — each a pure `(tc) => TestCase`
 * applying ONLY this action's predicted change and bumping `version` by 1 (the
 * server increments the per-case version by exactly 1 per action), so a
 * human-paced chained action on the same case sends the right If-Match and is
 * accepted (SC-004). `onSuccess` then overwrites with the server's authoritative
 * case; `onError` restores the pre-action snapshot (version included). There is
 * deliberately NO content updater — edit-content splices on success only (R3).
 */
export const includeTestCaseLocally = (testCase: TestCase): TestCase => ({
  ...testCase,
  inclusionDecision: { state: 'included', source: 'user' },
  version: testCase.version + 1,
});

export const excludeTestCaseLocally = (testCase: TestCase): TestCase => ({
  ...testCase,
  inclusionDecision: { state: 'excluded', source: 'user' },
  version: testCase.version + 1,
});

export const clearInclusionLocally = (testCase: TestCase): TestCase => ({
  ...testCase,
  inclusionDecision: { state: 'undecided' },
  version: testCase.version + 1,
});

export const markCompleteLocally = (testCase: TestCase): TestCase => ({
  ...testCase,
  completionState: { state: 'complete' },
  version: testCase.version + 1,
});

// F-038 US2: the optimistic inverse of markCompleteLocally — Reopen returns the
// case to incomplete (the only field it touches), bumping the version.
export const reopenLocally = (testCase: TestCase): TestCase => ({
  ...testCase,
  completionState: { state: 'incomplete' },
  version: testCase.version + 1,
});

export const setRolesLocally =
  (roles: readonly string[]) =>
  (testCase: TestCase): TestCase => ({
    ...testCase,
    roles: [...roles],
    version: testCase.version + 1,
  });

/**
 * Optimistic title patch. A defined title sets it; `undefined` CLEARS it (the
 * server restores the function/Test-ID fallback) — so we drop the property
 * entirely rather than store `title: undefined`, which `exactOptionalPropertyTypes`
 * forbids and which would otherwise leave the stale title in place.
 */
export const setTitleLocally =
  (title: string | undefined) =>
  (testCase: TestCase): TestCase => {
    if (title === undefined) {
      const next = { ...testCase, version: testCase.version + 1 };
      delete next.title;
      return next;
    }
    return { ...testCase, title, version: testCase.version + 1 };
  };

/**
 * Optimistic Function patch (F-033). A defined value sets it; `undefined` CLEARS
 * it (the server restores the 'Unassigned' placeholder) — so we drop the property
 * entirely rather than store `function: undefined`, which `exactOptionalPropertyTypes`
 * forbids and which would otherwise leave the stale value in place. Mirrors
 * `setTitleLocally`.
 */
export const setFunctionLocally =
  (fn: string | undefined) =>
  (testCase: TestCase): TestCase => {
    if (fn === undefined) {
      const next = { ...testCase, version: testCase.version + 1 };
      delete next.function;
      return next;
    }
    return { ...testCase, function: fn, version: testCase.version + 1 };
  };

// ── Feature 133: optimistic instructive-node decision reconciliation ─────────
//
// The node-level Instructional-Section decisions (F-013) already RETURN the
// decided node (`Section`/`Subsection`), so the client reconciles its cache from
// the response instead of refetching the whole script. UNLIKE the per-case
// actions (F-029), these are versioned by the SHARED TSCRIPT META aggregate
// version (`script.version`), not a per-node version, and the response omits it —
// so the cache predicts the server's `+1` via RELATIVE steps (bump / raise-to /
// drop), never a snapshot-restore that would clobber a concurrent node decision
// or the non-optimistic `updateSubsectionInstructions` (research R3, I-INO-04).
// And UNLIKE a per-case splice, there is NO `inclusionRollup` recompute: an
// instructional node carries zero test cases (the decision is only valid on a
// zero-case node), so no case-derived indicator changes (R2).

/** A node that can carry an Instructional-Section Decision (Section or Subsection). */
type DecidableNode = { readonly inclusionDecision?: NonNullable<Section['inclusionDecision']> };

/**
 * Immutably replace one Section (by id) in the script tree, applying `update` to
 * it — the node twin of `replaceTestCaseInScript`, but with NO `inclusionRollup`
 * recompute (a decision target is always a zero-case node, R2). Returns the SAME
 * script reference when the id is absent (a true no-op — no spurious cache
 * notification). Sibling sections are returned untouched (same reference).
 */
export const replaceSectionInScript = (
  script: TestScript,
  sectionId: string,
  update: (section: Section) => Section,
): TestScript => {
  if (!script.sections.some((section) => section.id === sectionId)) return script;
  return {
    ...script,
    sections: script.sections.map((section) =>
      section.id === sectionId ? update(section) : section,
    ),
  };
};

/**
 * Immutably replace one Subsection (by id) across the Section → Subsection tree,
 * applying `update` to it. Node twin of `replaceTestCaseInScript` with NO
 * `inclusionRollup` recompute (R2). Sections/subsections that do not own the
 * target are returned untouched (same reference); an absent id is a true no-op
 * (same script reference).
 */
export const replaceSubsectionInScript = (
  script: TestScript,
  subsectionId: string,
  update: (subsection: Subsection) => Subsection,
): TestScript => {
  const owns = script.sections.some((section) =>
    section.subsections.some((sub) => sub.id === subsectionId),
  );
  if (!owns) return script;
  return {
    ...script,
    sections: script.sections.map((section) => {
      if (!section.subsections.some((sub) => sub.id === subsectionId)) return section;
      return {
        ...section,
        subsections: section.subsections.map((sub) =>
          sub.id === subsectionId ? update(sub) : sub,
        ),
      };
    }),
  };
};

/**
 * The three node-decision optimistic updaters — each a pure `(node) => node`
 * setting ONLY the node's `inclusionDecision`, mirroring the server's
 * `nextDecision` (`decide-node-inclusion.ts`): included/excluded carry
 * `source:'user'`; clear is bare `{ state:'undecided' }`. Generic over Section +
 * Subsection (both carry the same optional `InclusionDecision`). They DELIBERATELY
 * do not touch any version — nodes are versioned at the script root, and the
 * aggregate-version step is applied separately (relative, below). `onSuccess`
 * overwrites with the authoritative node regardless, so the optimistic
 * `decidedBy`/`decidedAt` are left unset.
 */
export const includeNodeLocally = <T extends DecidableNode>(node: T): T => ({
  ...node,
  inclusionDecision: { state: 'included', source: 'user' },
});

export const excludeNodeLocally = <T extends DecidableNode>(node: T): T => ({
  ...node,
  inclusionDecision: { state: 'excluded', source: 'user' },
});

export const clearNodeLocally = <T extends DecidableNode>(node: T): T => ({
  ...node,
  inclusionDecision: { state: 'undecided' },
});

/**
 * Relative aggregate-version steps for the optimistic node-decision cache
 * surgery (R3 / I-INO-04). `script.version` is a SINGLE shared counter (every
 * node decision + the non-optimistic `updateSubsectionInstructions` / structural
 * ops bump it), so the optimistic path adjusts it RELATIVELY — never a
 * snapshot-restore, which would clobber a concurrent writer:
 *   - `bumpScriptVersion`     — `onMutate` predicts the server's META `+1`.
 *   - `raiseScriptVersionTo`  — `onSuccess` sets `max(cached, predicted)`, so a
 *                               racing 409-refetch that read a pre-commit version
 *                               can never pull it below what this mutation proved.
 *   - `dropScriptVersion`     — `onError` withdraws exactly this mutation's `+1`.
 */
export const bumpScriptVersion = (script: TestScript): TestScript => ({
  ...script,
  version: script.version + 1,
});

export const dropScriptVersion = (script: TestScript): TestScript => ({
  ...script,
  version: script.version - 1,
});

export const raiseScriptVersionTo = (script: TestScript, predicted: number): TestScript => ({
  ...script,
  version: Math.max(script.version, predicted),
});
